/**
 * VerticalArticleRow — paginated vertical list powered by Embla Carousel.
 *
 * Uses Embla with `axis: 'y'` and `slidesToScroll: PAGE_SIZE` to handle
 * smooth vertical pagination, touch/swipe support, and native overflow
 * without clipping card shadows.
 */
'use client';
import * as React from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import {
  ArticleCard,
  type ArticleCardProps,
} from '@/hen-ui/composites/ArticleCard';
import { Button } from '@/hen-ui/atomics/Button';
import { Icon } from '@/hen-ui/atomics/Icon';
import { Link } from '@/hen-ui/atomics/Link';
import { Flex } from '@/hen-ui/composites/Flex';
import { cn } from '@/hen-ui/utils';
import { articleRowContainerVariants } from './article-row.variants';

/** Number of articles visible per page in the vertical layout. */
const PAGE_SIZE = 3;

function usePrefersReducedMotion() {
  const [prefersReduced, setPrefersReduced] = React.useState(false);
  React.useEffect(() => {
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReduced(mql.matches);
    const onChange = (e: MediaQueryListEvent) => setPrefersReduced(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return prefersReduced;
}

export const VerticalArticleRow = React.forwardRef<
  HTMLDivElement,
  {
    articles?: ArticleCardProps[];
    children?: React.ReactNode;
    link?: string;
    linkLabel?: string;
    /** Variant for the CTA `Link`. Omit for the base link color. */
    linkVariant?: React.ComponentProps<typeof Link>['variant'];
    className?: string;
  }
>(
  (
    { articles = [], children, link, linkLabel, linkVariant, className },
    ref,
  ) => {
    const prefersReducedMotion = usePrefersReducedMotion();

    const [emblaRef, emblaApi] = useEmblaCarousel({
      axis: 'x',
      align: 'start',
      slidesToScroll: 1,
      loop: true,
      containScroll: false,
      duration: prefersReducedMotion ? 0 : 20,
      watchDrag: false,
    });

    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);

    React.useEffect(() => {
      if (!emblaApi) return;
      const onUpdate = () => {
        setCanScrollPrev(emblaApi.canScrollPrev());
        setCanScrollNext(emblaApi.canScrollNext());
      };
      onUpdate();
      emblaApi.on('select', onUpdate);
      emblaApi.on('reInit', onUpdate);
      return () => {
        emblaApi.off('select', onUpdate);
        emblaApi.off('reInit', onUpdate);
      };
    }, [emblaApi]);

    const childNodes = children ? React.Children.toArray(children) : null;
    const totalItems = childNodes ? childNodes.length : articles.length;
    const showArrows = !link && totalItems > PAGE_SIZE;

    const allItems = childNodes
      ? childNodes
      : articles.map((article, index) => (
          <ArticleCard key={article.href ?? `article-${index}`} {...article} />
        ));

    // Group items into pages of PAGE_SIZE
    const pages: React.ReactNode[][] = [];
    const itemsToPage = link ? allItems.slice(0, PAGE_SIZE) : allItems;
    for (let i = 0; i < itemsToPage.length; i += PAGE_SIZE) {
      pages.push(itemsToPage.slice(i, i + PAGE_SIZE));
    }

    return (
      <Flex
        ref={ref}
        direction='column'
        gap='md'
        className={cn(
          articleRowContainerVariants({ variant: 'vertical' }),
          'justify-between h-full',
          className,
        )}
      >
        <div ref={emblaRef} className='overflow-hidden -mx-2 px-2 -my-2 py-2'>
          <div className='flex'>
            {pages.map((page, pageIndex) => (
              <div
                key={pageIndex}
                className='min-w-0 shrink-0 grow-0 basis-full px-2'
              >
                <Flex
                  direction='column'
                  gap='md'
                  role='list'
                  aria-label='Article list'
                >
                  {page.map((item, itemIndex) => (
                    <div key={itemIndex} role='listitem'>
                      {item}
                    </div>
                  ))}
                </Flex>
              </div>
            ))}
          </div>
        </div>

        {link ? (
          // CTA uses HEN body-5 typography (text-b5) to match the carousel
          // treatment; Link's size prop only exposes text-sm/base/lg, so the
          // token is applied via className.
          <Flex direction='row' gap='xs' justify='end'>
            <Link
              href={link}
              variant={linkVariant}
              weight='bold'
              className='text-b5'
            >
              {linkLabel}
            </Link>
          </Flex>
        ) : showArrows ? (
          <Flex direction='row' gap='xs' justify='end'>
            <Button
              variant='grayOutline'
              size='sm'
              className='p-0 w-8 h-8 rounded-sm min-h-8 min-w-8 shrink-0'
              onClick={() => emblaApi?.scrollPrev()}
              disabled={!canScrollPrev}
              aria-label='Previous articles'
            >
              <Icon icon='arrow-left' />
            </Button>
            <Button
              variant='grayOutline'
              size='sm'
              className='p-0 w-8 h-8 rounded-sm min-h-8 min-w-8 shrink-0'
              onClick={() => emblaApi?.scrollNext()}
              disabled={!canScrollNext}
              aria-label='Next articles'
            >
              <Icon icon='arrow-right' />
            </Button>
          </Flex>
        ) : null}
      </Flex>
    );
  },
);

VerticalArticleRow.displayName = 'VerticalArticleRow';
